// client/src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import {
  Form, Button, Card, Spinner, Row, Col, Image, InputGroup
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import logo from '../assets/logo-c.png';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState({ email: '', password: '', general: '' });
  const [touched, setTouched] = useState({ email: false, password: false });

  const [loading, setLoading] = useState(false);

  const {
    login,
    isAuthenticated,
    authPageMessage,
    setAuthPageMessage,
    user
  } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      const targetPath = user.role === 'Dealer' ? '/dealer-dashboard' : '/dashboard';
      navigate(targetPath, { replace: true });
    } else {
      setAuthPageMessage('', '');
    }
  }, [isAuthenticated, user, navigate, setAuthPageMessage]);

  useEffect(() => {
    if (authPageMessage) {
      setErrors(prev => ({ ...prev, general: authPageMessage }));
    } else {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  }, [authPageMessage]);

  const validate = () => {
    const newErrors = { email: '', password: '', general: '' };
    const emailTrim = email.trim();

    if (!emailTrim) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!password) {
      newErrors.password = 'Password is required.';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthPageMessage('', '');
    setErrors(prev => ({ ...prev, general: '' }));
    setTouched({ email: true, password: true });

    if (!validate()) return;

    setLoading(true);
    await login(email.trim(), password);
    setLoading(false);
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column justify-content-center align-items-center p-3"
      style={{ background: 'linear-gradient(#4594ad, white, white)' }}
    >
      {/* Logo + Branding */}
      <div className="text-center mb-0 mt-0">
        <Image
          src={logo}
          alt="Company Logo"
          className="mb-3 img-fluid"
          style={{ maxWidth: '250px', height: 'auto' }}
        />
        <h1 className="mt-5 mb-0 text-black ">Touchless Tracking</h1>
      </div>

      {/* Login Card */}
      <Card className="p-4 mt-4 shadow rounded border" style={{ maxWidth: '450px', width: '100%' }}>
        <Card.Body>
          <h4 className="text-center mb-4">Login</h4>

          <Form noValidate onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formLoginEmail">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                isInvalid={touched.email && !!errors.email}
                disabled={loading}
                autoComplete="username"
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formLoginPassword">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                  isInvalid={touched.password && !!errors.password}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(prev => !prev)}
                  disabled={loading}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
              </InputGroup>

              {/* Forgot Password Link */}
              <div className="text-end mt-2">
                <small>
                  <Link to="/forgot-password" className="fw-bold text-decoration-none">Forgot Password?</Link>
                </small>
              </div>
            </Form.Group>

            {errors.general && (
              <div className="text-danger small mb-3 text-center" role="alert">
                {errors.general}
              </div>
            )}

            <Button
              variant="primary"
              type="submit"
              className="w-100 mt-3 fw-bold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Logging in...</span>
                </>
              ) : (
                'Login'
              )}
            </Button>
          </Form>

          <Row className="mt-4">
            <Col className="text-center">
              <small>
                Don&apos;t have an account?{' '}
                <a href="/signup" className="fw-bold text-decoration-none">Sign Up</a>
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}

export default LoginPage;
